import React, { useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, Search, Edit2, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import { inwardMoneyService } from '../services/supabaseService';
import { isSupabaseConfigured } from '../lib/supabaseClient';

const InwardMoney = () => {
  const [inwardMoney, setInwardMoney] = useSessionStorage('inwardMoney_records', [
    { id: 1, source: 'Initial Capital', amount: 500000, date: '2026-05-01', description: 'Starting funds' }
  ]);
  const [searchTerm, setSearchTerm] = useSessionStorage('inwardMoney_searchTerm', '');
  const [isModalOpen, setIsModalOpen] = useSessionStorage('inwardMoney_isModalOpen', false);
  const [editingRecord, setEditingRecord] = useSessionStorage('inwardMoney_editingRecord', null);

  const [formData, setFormData] = useSessionStorage('inwardMoney_formData', {
    source: '', amount: '', date: '', description: ''
  });

  const fetchRecords = async () => {
    if (!isSupabaseConfigured()) return;
    try {
      const data = await inwardMoneyService.getAll();
      if (Array.isArray(data) && data.length > 0) {
        setInwardMoney(data);
      }
    } catch (err) {
      console.error('Error fetching inward money from Supabase:', err);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        source: record.source,
        amount: record.amount,
        date: record.date ? record.date.split('T')[0] : '',
        description: record.description || ''
      });
    } else {
      setEditingRecord(null);
      const today = new Date().toISOString().split('T')[0];
      setFormData({ source: '', amount: '', date: today, description: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isSupabaseConfigured()) {
        if (editingRecord && editingRecord.id && typeof editingRecord.id === 'number') {
          await inwardMoneyService.update(editingRecord.id, formData);
        } else {
          await inwardMoneyService.create(formData);
        }
        await fetchRecords();
      } else {
        if (editingRecord) {
          setInwardMoney(inwardMoney.map(item => item.id === editingRecord.id ? { ...formData, id: editingRecord.id } : item));
        } else {
          setInwardMoney([...inwardMoney, { ...formData, id: Date.now() }]);
        }
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving inward money:', err);
      alert('Error saving inward money: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        if (isSupabaseConfigured()) {
          await inwardMoneyService.delete(id);
          await fetchRecords();
        } else {
          setInwardMoney(inwardMoney.filter(item => item.id !== id));
        }
      } catch (err) {
        console.error('Error deleting inward money:', err);
        alert('Error deleting inward money: ' + err.message);
      }
    }
  };

  const filteredRecords = inwardMoney.filter(record =>
    record.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2>Inward Money</h2>
          <p>Track capital, investments, and other non-rent income sources.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()}>
          <Plus size={16} /> Add Record
        </button>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <div className="relative" style={{ width: '300px' }}>
            <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Search source or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
            />
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Date</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Source</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Amount (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Description</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={record.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '1rem 0' }}>{record.date}</td>
                  <td style={{ padding: '1rem 0', fontWeight: '500', color: 'var(--text-primary)' }}>{record.source}</td>
                  <td style={{ padding: '1rem 0', fontWeight: '500', color: 'var(--accent-color)' }}>+{record.amount}</td>
                  <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>{record.description}</td>
                  <td style={{ padding: '1rem 0', textAlign: 'right' }}>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleOpenModal(record)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--text-secondary)' }}>
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(record.id)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--danger-color)' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRecords.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No records found.
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingRecord ? "Edit Record" : "Add Inward Money"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Source of Funds</label>
            <input required type="text" value={formData.source} onChange={e => setFormData({ ...formData, source: e.target.value })} placeholder="e.g. Owner Investment" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Amount (₹)</label>
              <input required type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="e.g. 50000" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Date</label>
              <input required type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Description</label>
            <textarea
              required
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Details about this money..."
              style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              rows="3"
            />
          </div>
          <button type="submit" className="btn-primary mt-4">
            {editingRecord ? 'Update Record' : 'Save Record'}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default InwardMoney;
